import { useEffect, useState } from 'react';
import {
  TrashIcon,
  ArrowPathIcon,
  PlayIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import {
  getStatus,
  run as runCleanup,
  RetentionStatus,
  RetentionRunSummary,
  PurgeStat,
  KeepStat,
} from '../api/retention';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(0)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function formatNumber(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString();
}

function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
}

function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// Disk usage bar color tiers (per spec).
function usageColors(percent: number): { text: string; bar: string } {
  if (percent >= 90) return { text: 'text-red-600', bar: 'bg-red-500' };
  if (percent >= 85) return { text: 'text-amber-600', bar: 'bg-amber-500' };
  return { text: 'text-emerald-600', bar: 'bg-emerald-500' };
}

function scopeBadge(scope: string, table?: string): { label: string; className: string } {
  if (scope === 'registry') {
    return { label: 'Registry', className: 'bg-violet-50 text-violet-700 border border-violet-200' };
  }
  return {
    label: table ? 'Tenant' : 'Tenant',
    className: 'bg-blue-50 text-blue-700 border border-blue-200',
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

/** See Backups.tsx — `embedded` drops the standalone page header when this
 *  renders inside the Database section's Retention tab. */
export default function DataRetention({ embedded = false }: { embedded?: boolean }) {
  const [status, setStatus] = useState<RetentionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<RetentionRunSummary | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  async function fetchStatus() {
    setLoading(true);
    setError(null);
    try {
      const res = await getStatus();
      setStatus(res.data);
    } catch {
      setError('Failed to load retention status.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchStatus();
  }, []);

  async function handleRun() {
    setRunning(true);
    setRunError(null);
    setRunResult(null);
    try {
      const res = await runCleanup();
      setRunResult(res.data);
      setConfirmOpen(false);
      await fetchStatus();
    } catch {
      setRunError('Cleanup run failed. Check server logs.');
    } finally {
      setRunning(false);
    }
  }

  const disk = status?.disk;
  const cutoffDays = status?.cutoffDays ?? 180;
  const purge = status?.stats?.purge ?? [];
  const keep = status?.stats?.keep ?? [];
  const lastRun = status?.stats?.recentRuns?.[0];

  const usage = usageColors(disk?.percentUsed ?? 0);

  return (
    <div className={embedded ? 'space-y-5' : 'p-6 space-y-5'}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          {!embedded && (
            <>
              <div className="text-xs text-slate-500 mb-1">System / Data Retention</div>
              <div className="flex items-center gap-3">
                <TrashIcon className="w-7 h-7 text-slate-600" />
                <h1 className="text-2xl font-bold text-slate-900">Data Retention</h1>
              </div>
            </>
          )}
          <p className="text-sm text-slate-500 mt-1">
            {cutoffDays}-day retention runs automatically (daily 04:30 via scheduler). Review disk,
            see what&apos;s purgeable, or run it now.
          </p>
        </div>
        <button
          onClick={fetchStatus}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500 text-sm">
          <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
          Loading…
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <p className="text-red-600 text-sm">{error}</p>
          <button
            onClick={fetchStatus}
            className="px-4 py-2 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {/* ── Disk cards ─────────────────────────────────────────────── */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
              Disk
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Root FS used */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400">
                  Root FS used
                </div>
                <div className={`text-xl font-bold mt-1 ${usage.text}`}>
                  {(disk?.percentUsed ?? 0).toFixed(0)}%
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${usage.bar}`}
                    style={{ width: `${Math.min(disk?.percentUsed ?? 0, 100)}%` }}
                  />
                </div>
                <div className="text-[11px] text-slate-500 mt-1">threshold 85%</div>
              </div>
              {/* Free space */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400">
                  Free space
                </div>
                <div className="text-xl font-bold mt-1 text-slate-900">
                  {formatBytes(disk?.freeBytes)}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  of {formatBytes(disk?.totalBytes)}
                </div>
              </div>
              {/* Log files */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400">
                  Log files
                </div>
                <div className="text-xl font-bold mt-1 text-slate-900">
                  {formatBytes(disk?.logDirBytes)}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">log directory</div>
              </div>
              {/* Backups dir */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400">
                  Backups dir
                </div>
                <div className="text-xl font-bold mt-1 text-slate-900">
                  {formatBytes(disk?.backupsDirBytes)}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">backup volume</div>
              </div>
            </div>
          </div>

          {/* ── Purged at 180 days ─────────────────────────────────────── */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
              Purged at {cutoffDays} days
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      Table
                    </th>
                    <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      Scope
                    </th>
                    <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      Total rows
                    </th>
                    <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      Older than {cutoffDays}d
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {purge.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-3 py-6 text-center text-slate-400 text-xs">
                        No purge tables.
                      </td>
                    </tr>
                  ) : (
                    purge.map((row: PurgeStat) => {
                      const badge = scopeBadge(row.scope);
                      const isTenant = row.scope === 'tenant';
                      return (
                        <tr key={row.table} className="hover:bg-slate-50 transition-colors">
                          <td className="px-3 py-2 font-mono text-xs text-slate-800">
                            {row.table}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold ${badge.className}`}
                            >
                              {badge.label}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                            {formatNumber(row.totalRows)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-bold text-amber-600">
                            {isTenant && row.purgeableRows === null
                              ? 'per-tenant'
                              : formatNumber(row.purgeableRows)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Kept indefinitely ──────────────────────────────────────── */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
              Kept indefinitely (compliance)
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      Table
                    </th>
                    <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      Scope
                    </th>
                    <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      Total rows
                    </th>
                    <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {keep.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-3 py-6 text-center text-slate-400 text-xs">
                        No compliance tables.
                      </td>
                    </tr>
                  ) : (
                    keep.map((row: KeepStat) => {
                      const badge = scopeBadge(row.scope);
                      return (
                        <tr key={row.table} className="opacity-60">
                          <td className="px-3 py-2 font-mono text-xs text-slate-800">
                            {row.table}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold ${badge.className}`}
                            >
                              {badge.label}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                            {formatNumber(row.totalRows)}
                          </td>
                          <td className="px-3 py-2">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold bg-slate-100 text-slate-500 border border-slate-200">
                              Never purged
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Run ────────────────────────────────────────────────────── */}
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
              Run
            </div>
            <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3">
              <div className="text-sm text-slate-600">
                {lastRun ? (
                  <>
                    Last run: <b className="text-slate-900">{formatDateTime(lastRun.started_at)}</b> ·
                    purged{' '}
                    <b className="text-slate-900">
                      {formatNumber((lastRun.registry_rows ?? 0) + (lastRun.tenant_rows ?? 0))} rows
                    </b>{' '}
                    + <b className="text-slate-900">{formatNumber(lastRun.files_deleted)} log files</b> ·{' '}
                    {formatDuration(lastRun.duration_ms)}
                  </>
                ) : (
                  <span className="text-slate-400">No runs recorded yet.</span>
                )}
              </div>
              <button
                onClick={() => {
                  setRunError(null);
                  setConfirmOpen(true);
                }}
                disabled={running}
                className="ml-auto flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <PlayIcon className="w-4 h-4" />
                Run cleanup now
              </button>
            </div>

            {/* Success summary */}
            {runResult && (
              <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                <div className="flex items-center gap-2 text-emerald-800 text-sm font-semibold">
                  <CheckCircleIcon className="w-5 h-5" />
                  Cleanup complete — purged{' '}
                  {formatNumber(runResult.registry.total + runResult.tenant.total)} rows +{' '}
                  {formatNumber(runResult.files)} files in {formatDuration(runResult.durationMs)}.
                </div>
                {Object.keys(runResult.registry.byTable ?? {}).length > 0 && (
                  <ul className="mt-2 text-xs text-emerald-700 space-y-0.5">
                    {Object.entries(runResult.registry.byTable).map(([table, n]) => (
                      <li key={table} className="font-mono">
                        {table}: {formatNumber(n)}
                      </li>
                    ))}
                    <li>
                      tenant ({formatNumber(runResult.tenant.tenants)} companies):{' '}
                      {formatNumber(runResult.tenant.total)}
                    </li>
                  </ul>
                )}
              </div>
            )}

            {/* Run error (outside dialog) */}
            {runError && !confirmOpen && (
              <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm">
                {runError}
              </div>
            )}

            <p className="text-[11px] text-slate-400 mt-3">
              Cutoff: rows older than {cutoffDays} days. Registry tables purge in one pass; the tenant
              table fans out across all active companies. Batched to avoid locks. The KEEP tables are
              shown for transparency and are never targeted by the job.
            </p>
          </div>
        </>
      )}

      {/* ── Confirm dialog ───────────────────────────────────────────── */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <ExclamationTriangleIcon className="w-6 h-6 text-amber-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">Run cleanup now?</h2>
              </div>
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={running}
                className="p-1 rounded-lg hover:bg-slate-100 disabled:opacity-50"
                aria-label="Close"
              >
                <XMarkIcon className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <p className="text-sm text-slate-600 mt-4">
              Delete rows older than {cutoffDays} days from the retention tables? Compliance tables
              are never touched.
            </p>
            {runError && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-xs">
                {runError}
              </div>
            )}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={running}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRun}
                disabled={running}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {running ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    Running…
                  </>
                ) : (
                  <>
                    <PlayIcon className="w-4 h-4" />
                    Run cleanup
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
